/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 *
 * @author Satyaki Bose
 * @file lateWorkCustomDateInputUtils.js
 */

import { formatDateToStorage, parseDate } from '../../../../common/dateTimeUtils';

const MASKED_DATE_PATTERN = /^(\d{2})\/(\d{2})\/(\d{4})$/;

export function formatDateMaskInput(raw) {
  const digits = String(raw || '').replace(/\D/g, '').slice(0, 8);

  if (digits.length === 0) {
    return '';
  }

  if (digits.length <= 2) {
    return digits.length === 2 ? `${digits}/` : digits;
  }

  if (digits.length <= 4) {
    const month = digits.slice(0, 2);
    const day = digits.slice(2);

    return digits.length === 4 ? `${month}/${day}/` : `${month}/${day}`;
  }

  const month = digits.slice(0, 2);
  const day = digits.slice(2, 4);
  const year = digits.slice(4);

  return `${month}/${day}/${year}`;
}

export function formatInputDateFromStorage(dateStr) {
  const date = parseDate(dateStr);
  if (!date) {
    return '';
  }

  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();

  return `${month}/${day}/${year}`;
}

export function parseMaskedDateInput(masked, { minDate = null } = {}) {
  if (!masked?.trim()) {
    return null;
  }

  const trimmed = masked.trim();
  const match = trimmed.match(MASKED_DATE_PATTERN);
  if (!match) {
    return null;
  }

  const [, month, day, year = ''] = match;

  if (year.length !== 4) {
    return null;
  }
  const parsed = new Date(Number(year), Number(month) - 1, Number(day));

  if (
    Number.isNaN(parsed.getTime())
    || parsed.getFullYear() !== Number(year)
    || parsed.getMonth() !== Number(month) - 1
    || parsed.getDate() !== Number(day)
  ) {
    return null;
  }

  if (minDate) {
    const minDay = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());
    const parsedDay = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());

    if (parsedDay < minDay) {
      return null;
    }
  }

  return formatDateToStorage(parsed);
}

export function isValidMaskedDateInput(masked, options) {
  return Boolean(parseMaskedDateInput(masked, options));
}
